import { useLocation } from 'wouter';
import { Mail, Bell } from 'lucide-react';
import type { UserAPI } from '@shared/types/api';
import { useSettings, useUpdateSettings } from '@/lib/api';
import { PageHeader } from '@/components/ux/PageHeader';
import { Loader } from '@/components/Loader';

function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
        checked ? 'bg-primary-500' : 'bg-neutral-300 dark:bg-neutral-600'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

function SettingRow({
  icon,
  title,
  description,
  checked,
  onChange,
  disabled,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-row items-center gap-3 p-4 card">
      <span className="w-11 h-11 rounded-xl bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center flex-shrink-0">
        {icon}
      </span>
      <span className="min-w-0 flex-1 flex flex-col">
        <span className="font-semibold text-base dark:text-neutral-100 leading-tight">{title}</span>
        <span className="text-xs text-dark-muted dark:text-neutral-400">{description}</span>
      </span>
      <Toggle checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  );
}

export default function NotificationSettings() {
  const [, navigate] = useLocation();
  const { data, isLoading } = useSettings({ staleTime: 0 });
  const updateSettings = useUpdateSettings();

  const settings: UserAPI.UserSettings = data?.data ?? {
    weeklyEventSuggestionsEmail: true,
    recommendedEventsNotification: true,
  };

  const update = (patch: Partial<UserAPI.UserSettings>) => {
    updateSettings.mutate(patch);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <Loader />
      </div>
    );
  }

  return (
    <div className="pt-4 pb-24 md:pt-8 px-4">
      <div className="max-w-2xl mx-auto">
        <PageHeader title="Notifications" onBack={() => navigate('/profile')} />

        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-dark-text px-1 mb-1">Communication</h3>

          <SettingRow
            icon={<Mail className="w-5 h-5 text-primary-600 dark:text-primary-400" />}
            title="Weekly event suggestions email"
            description="A weekly digest of events picked for you"
            checked={settings.weeklyEventSuggestionsEmail}
            onChange={(v) => update({ weeklyEventSuggestionsEmail: v })}
            disabled={updateSettings.isPending}
          />

          <SettingRow
            icon={<Bell className="w-5 h-5 text-primary-600 dark:text-primary-400" />}
            title="Recommended events notification"
            description="Get notified when new events match your interests"
            checked={settings.recommendedEventsNotification}
            onChange={(v) => update({ recommendedEventsNotification: v })}
            disabled={updateSettings.isPending}
          />
        </div>
      </div>
    </div>
  );
}
